import { useEffect, useMemo } from 'react';

import { useFetchData } from '@/api/apiHooks';
import { useNavigationContext } from '@/context/NavigationContext';
import { PanelsResponse, PanelsResponsePanel } from '@/utils/types';
import { URLS } from '@/utils/urls';

interface UsePanelIdOrDefaultResult {
  panelId: string | undefined;
  companyId: string | undefined;
  isLoading: boolean;
}

export const usePanelIdOrDefault = (
  panelId: string | undefined,
  enabled: boolean
): UsePanelIdOrDefaultResult => {
  const { panelId: storedPanelId, setPanelContext } = useNavigationContext();

  const shouldFetchPanels = enabled && !panelId && !storedPanelId;

  const { data, isLoading } = useFetchData<PanelsResponse>({
    url: URLS.getAllPanels(),
    params: { page_size: 1 },
    enabled: shouldFetchPanels,
  });

  const resolvedPanelId = useMemo(
    () => panelId ?? storedPanelId ?? data?.data?.[0]?.id,
    [panelId, storedPanelId, data]
  );

  const { data: panelDetail, isLoading: isPanelDetailLoading } =
    useFetchData<PanelsResponsePanel>({
      url: URLS.getPanel(resolvedPanelId),
      enabled: enabled && !!resolvedPanelId,
    });

  const resolvedCompanyId = panelDetail?.company_id;

  useEffect(() => {
    if (resolvedPanelId && resolvedCompanyId) {
      setPanelContext(resolvedPanelId, resolvedCompanyId);
    }
  }, [resolvedPanelId, resolvedCompanyId, setPanelContext]);

  return {
    panelId: resolvedPanelId,
    companyId: resolvedCompanyId,
    isLoading:
      (shouldFetchPanels && isLoading) ||
      (enabled && !!resolvedPanelId && isPanelDetailLoading),
  };
};
